import React, { useState, useEffect } from "react";
import { Edit3, Save, Copy, Bot, Mail } from "lucide-react";
import toast from "react-hot-toast";
import ApiService from "../../services/ApiService";

// Validation utilities
const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const validateDomain = (domain) => {
  const cleanDomain = domain.trim();
  // Require HTTPS protocol
  if (!cleanDomain.startsWith("https://")) {
    return false;
  }

  try {
    const url = new URL(cleanDomain);
    const hostname = url.hostname;
    // Validate hostname format
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-]{2,}$/;
    return domainRegex.test(hostname);
  } catch {
    return false;
  }
};

function AboutSection() {
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [aboutData, setAboutData] = useState({
    organization_id: "",
    organization_name: "",
    organization_email: "",
    organization_domain: "",
    organization_context: "",
    custom_prompt: "",
    ai_response_in_admin_email_enabled: false,
  });

  const [formData, setFormData] = useState({ ...aboutData });
  const [errors, setErrors] = useState({});
  const [updating, setUpdating] = useState(false);
  const [aiToggleSaving, setAiToggleSaving] = useState(false);

  useEffect(() => {
    localStorage.setItem('loading', loading);
  }, [loading]);

  const loadContent = async () => {
    setLoading(true);

    try {
      const data = await ApiService.getOrganizationData();

      const cleanData = {
        organization_id: data.organization_id || window.Clerk?.organization?.id || localStorage.getItem('uttik_organization_id') || "",
        organization_name: data.organization_name || "",
        organization_email: data.organization_email || "",
        organization_domain: data.organization_domain || "",
        organization_context: data.organization_context || "",
        custom_prompt: data.custom_prompt || "",
        ai_response_in_admin_email_enabled: data.ai_response_in_admin_email_enabled ?? false,
      };

      setAboutData(cleanData);
      setFormData(cleanData);
    } catch {
      toast.error("Failed to load organization data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContent();
  }, []);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    // Validate organization name
    if (!formData.organization_name.trim()) {
      newErrors.organization_name = "Organization name is required";
    } else if (formData.organization_name.trim().length < 2) {
      newErrors.organization_name = "Name must be at least 2 characters";
    }
    // Validate email
    if (!formData.organization_email.trim()) {
      newErrors.organization_email = "Support email is required";
    } else if (!validateEmail(formData.organization_email)) {
      newErrors.organization_email = "Please enter a valid email";
    }
    // Validate domain
    if (!formData.organization_domain.trim()) {
      newErrors.organization_domain = "Domain is required";
    } else if (!validateDomain(formData.organization_domain)) {
      newErrors.organization_domain = "Please enter a valid HTTPS URL (e.g., https://example.com)";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAiToggle = async (newValue) => {
    setAiToggleSaving(true);
    // Optimistically update UI
    setFormData((prev) => ({ ...prev, ai_response_in_admin_email_enabled: newValue }));
    setAboutData((prev) => ({ ...prev, ai_response_in_admin_email_enabled: newValue }));
    try {
      await ApiService.updateOrgFields({ ai_response_in_admin_email_enabled: newValue });
      toast.success(
        newValue
          ? "AI response in admin emails enabled"
          : "AI response in admin emails disabled"
      );
    } catch {
      // Revert on failure
      setFormData((prev) => ({ ...prev, ai_response_in_admin_email_enabled: !newValue }));
      setAboutData((prev) => ({ ...prev, ai_response_in_admin_email_enabled: !newValue }));
      toast.error("Failed to update setting");
    } finally {
      setAiToggleSaving(false);
    }
  };

  const handleSave = async () => {
    // Validate form before saving
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }
    setUpdating(true);

    try {
      await ApiService.updateOrgFields(formData);
      
      // Trigger category seeding (will only create if none exist for this org)
      try {
        await ApiService.seedCategories();
      } catch (seedError) {
        console.error("Failed to seed categories", seedError);
        // We don't block the UI for seeding failure
      }

      toast.success("Changes saved!");
      setAboutData(formData);
      setEditMode(false);
      setErrors({});
    } catch {
      toast.error("Failed to save changes");
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    setFormData(aboutData);
    setEditMode(false);
    setErrors({});
  };

  const handleCopyOrgId = () => {
    const orgId = aboutData.organization_id || formData.organization_id;
    if (orgId) {
      navigator.clipboard.writeText(orgId);
      toast.success("Organization ID copied!");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[90vh]">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5 about">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent leading-tight">
            Organization Settings
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Manage your organization profile, AI behaviour, and notification preferences
          </p>
        </div>

        <div className="flex gap-2">
          {!editMode ? (
            <button
              onClick={() => setEditMode(true)}
              className="px-4 py-2 bg-black text-white rounded-lg text-sm font-semibold hover:bg-gray-800 transition-all shadow-sm flex items-center gap-2"
            >
              <Edit3 className="w-4 h-4" />
              Edit
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Discard
              </button>
              <button
                onClick={handleSave}
                disabled={updating}
                className="flex items-center gap-2 px-6 py-2 bg-black text-white rounded-lg text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                {updating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Org ID pill */}
      <div className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg w-fit text-sm shadow-sm">
        <span className="text-gray-600">Organization ID: <span className="text-gray-900 font-medium">{aboutData.organization_id || "Not available"}</span></span>
        <button onClick={handleCopyOrgId} className="text-gray-500 hover:text-gray-700 transition-colors focus:outline-none">
          <Copy className="w-4 h-4" />
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 bg-white">
          <h3 className="font-bold text-gray-900 text-lg">Organization Details</h3>
        </div>
        
        <div className="p-6 space-y-6">
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-700">Organization Name</label>
            <input
              type="text"
              name="organization_name"
              value={formData.organization_name}
              onChange={handleFormChange}
              placeholder="Enter organization name"
              disabled={!editMode}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-black/5 focus:border-black outline-none transition-all text-sm ${!editMode ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}
            />
            {errors.organization_name && <p className="text-xs text-red-500 mt-1">{errors.organization_name}</p>}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-700">Support Email</label>
            <input
              type="email"
              name="organization_email"
              value={formData.organization_email}
              onChange={handleFormChange}
              placeholder="support@example.com"
              disabled={!editMode}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-black/5 focus:border-black outline-none transition-all text-sm ${!editMode ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}
            />
            {errors.organization_email && <p className="text-xs text-red-500 mt-1">{errors.organization_email}</p>}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-700">Domain</label>
            <input
              type="url"
              name="organization_domain"
              value={formData.organization_domain}
              onChange={handleFormChange}
              placeholder="https://example.com"
              disabled={!editMode}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-black/5 focus:border-black outline-none transition-all text-sm ${!editMode ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}
            />
            {errors.organization_domain && <p className="text-xs text-red-500 mt-1">{errors.organization_domain}</p>}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-700">Custom Prompt</label>
            {!editMode && !formData.custom_prompt ? (
              <div className="p-5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600">
                <p className="mb-4">Click on &quot;Edit&quot; to add Custom Prompt</p>
                <div>
                  <p className="font-medium text-gray-700 mb-2">Examples:</p>
                  <ol className="list-decimal list-outside ml-4 space-y-2">
                    <li><strong>Add brand-specific instructions:</strong> &quot;When talking about our product, emphasize its eco-friendly and sustainable features.&quot;</li>
                    <li><strong>Tailor language:</strong> &quot;Respond in the language of the inquiry. If a customer&apos;s email is in Spanish, reply in Spanish. If it&apos;s in French, reply in French.&quot;</li>
                    <li><strong>Define persona and tone:</strong> &quot;Act as a friendly support agent for a fitness brand. Your responses should be positive and encouraging.&quot;</li>
                  </ol>
                </div>
              </div>
            ) : (
              <textarea
                name="custom_prompt"
                value={formData.custom_prompt}
                onChange={handleFormChange}
                placeholder="Enter custom prompt"
                disabled={!editMode}
                rows={4}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-black/5 focus:border-black outline-none transition-all text-sm ${!editMode ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}
              />
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-700">Organization Context for AI</label>
            <div className="p-5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700">
              <p className="mb-4">Here&apos;s the contextual information for {formData.organization_domain ? formData.organization_domain.replace(/^https?:\/\//, '').replace(/\/$/, '') : "your domain"}, including the provided support email:</p>
              <ul className="list-disc list-outside ml-4 space-y-2">
                <li><strong>Official support contact:</strong> {formData.organization_email || "Not provided"}</li>
                <li><strong>Domain Detection:</strong> Could not determine domain.</li>
                <li><strong>Niche Identification:</strong> Could not determine Niche.</li>
                <li><strong>Search Intent:</strong> Could not determine Search Intent.</li>
                <li><strong>User Persona Extraction:</strong> A user persona represents your target user, including their needs, wants, goals, problems, and other characteristics. It helps teams visualize their ideal customer and understand the person they&apos;re building a product for. User personas humanize data, generate empathy, and define the future user, promoting user-centricity. They are based on user research insights and provide valuable information for making informed design decisions. A good user persona includes demographics, behavior patterns, motivations, and goals.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* AI Response in Admin Email Toggle */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 bg-white">
          <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
            <Bot className="w-5 h-5 text-gray-700" />
            AI & Automation Settings
          </h3>
        </div>

        <div className="p-6">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Mail className="w-4 h-4 text-gray-600" />
                <p className="text-sm font-semibold text-gray-900">
                  Include AI Response in Admin Notification Email
                </p>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">
                When enabled, the full AI-generated response sent to the customer will also be
                embedded in your admin notification email — so you can review exactly what your
                AI replied. When disabled, the notification only confirms that{" "}
                <span className="italic text-gray-600">"Automated AI Response has been sent to user"</span>,
                keeping emails concise.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                    formData.ai_response_in_admin_email_enabled
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-gray-100 text-gray-500 border border-gray-200"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      formData.ai_response_in_admin_email_enabled ? "bg-emerald-500" : "bg-gray-400"
                    }`}
                  />
                  {formData.ai_response_in_admin_email_enabled ? "Active" : "Inactive"}
                </span>
                {aiToggleSaving && (
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <div className="w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                    Saving…
                  </span>
                )}
              </div>
            </div>

            {/* Toggle Switch */}
            <button
              onClick={() => handleAiToggle(!formData.ai_response_in_admin_email_enabled)}
              disabled={aiToggleSaving}
              aria-pressed={formData.ai_response_in_admin_email_enabled}
              aria-label="Toggle AI response in admin email"
              className={`relative flex-shrink-0 inline-flex items-center w-12 h-6 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black/20 ${
                aiToggleSaving ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
              } ${
                formData.ai_response_in_admin_email_enabled
                  ? "bg-black"
                  : "bg-gray-200"
              }`}
            >
              <span
                className={`inline-block w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                  formData.ai_response_in_admin_email_enabled ? "translate-x-6" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AboutSection;

